import Box from '@mui/material/Box';
import type { ReactNode } from 'react';

/**
 * Layout for the "what happened at this moment" block that sits above the
 * traces on both the Dashboard's Cognitive trace and the Analysis overlay:
 * the recording(s) on the left, what was said (and where everyone was) on the
 * right.
 *
 * Why a shared component rather than two Stacks: these two screens have drifted
 * apart twice already, and the whole point of this block is that the same
 * gesture — click a trace, click a caption — behaves the same on both. Keeping
 * the proportions and the breakpoint in one place means a change to one screen
 * cannot quietly not-happen to the other.
 *
 * A grid rather than a flex row, because the two columns need a *predictable*
 * split: with `flex: 1 1 58%` beside `flex: 1`, the second column grows from a
 * zero basis and the pair settles near 79/21 — the transcript ended up much
 * narrower than the 58/42 the code claimed. `fr` units say what they mean.
 *
 * Below `md` the columns stack (media first): side by side on a laptop
 * half-screen leaves both halves too narrow to be worth it.
 */
export function MomentInspector({
  media,
  aside,
  columns = '3fr 2fr',
}: {
  media: ReactNode;
  /** Transcript, readouts — whatever tells you about the pinned moment. */
  aside?: ReactNode;
  /**
   * `grid-template-columns` above `md`. A media column carrying several
   * cameras side by side earns more of the row than a single clip does.
   */
  columns?: string;
}) {
  if (!aside) return <>{media}</>;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: columns },
        gap: 1.5,
        alignItems: 'stretch',
        // Floor for the case where the media side is short — a recording with
        // no video is a one-line panel, and the aside shouldn't collapse to
        // match it.
        minHeight: { md: 220 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>{media}</Box>
      {/* The aside is taken OUT OF FLOW above `md` (absolute inside a relative
          cell) so its content can't stretch the row. Stretching is exactly
          what a grid does otherwise: both columns get the height of the taller
          one, and since a long transcript is taller than the clips, the row
          grew to fit it and the box never scrolled — it just ran past the
          bottom of the video. Out of flow, the row's height comes from the
          media alone, the aside fills that height, and the overflow scrolls.
          Stacked (below `md`) it flows normally, where growing is right. */}
      <Box sx={{ position: { md: 'relative' }, minWidth: 0, minHeight: 0 }}>
        <Box
          sx={{
            position: { md: 'absolute' },
            inset: { md: 0 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minHeight: 0,
          }}
        >
          {aside}
        </Box>
      </Box>
    </Box>
  );
}
